import { useEffect } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, type MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import type { ControlledShareButtonIcon } from '../controlled/ControlledShareButton.tsx';
import {
	ACCESS_LINK_ENROLLED_CONTENT_TYPES,
	SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
} from '../controller/shareAndRestrictDialogConfig.ts';

import type {
	ConfluenceShareableLinkAccessType,
	useShareAndRestrictButtonDataQuery$data,
	useShareAndRestrictButtonDataQuery$variables,
} from './__generated__/useShareAndRestrictButtonDataQuery.graphql.ts';

const useShareAndRestrictButtonDataQuery = graphql`
	query useShareAndRestrictButtonDataQuery(
		$contentId: ID!
		$isContentTypePublicLinkEligible: Boolean!
	) {
		singleContent(id: $contentId) {
			id
			hasInheritedRestrictions
			hasRestrictions
			hasViewRestrictions
			title
		}
		publicLinkPage(pageId: $contentId) @include(if: $isContentTypePublicLinkEligible) {
			pageId
			pageStatus
			accessType
		}
	}
`;

const tooltipMessages = defineMessages({
	restricted: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.restricted',
		defaultMessage: 'Restricted - Only specific people can access',
		description: 'Tooltip text when the current access state of the content is "Restricted"',
	},
	inherited: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.inherited',
		defaultMessage: 'Restricted by a parent - Anyone on the parent item can access',
		description:
			'Tooltip text when the current access state of the content is "Restricted by a parent"',
	},
	draft: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.draft',
		defaultMessage: 'Open - Anyone in the space (with the link) can access',
		description:
			'Tooltip text when the current access state of the content is an unpublished draft',
	},
	openEdit: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.openEdit',
		defaultMessage: 'Open - Anyone in the space can edit',
		description: 'Tooltip text when the current access state of the content is "Open for edit"',
	},
	openView: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.openView',
		defaultMessage: 'Open - Anyone in the space can view',
		description: 'Tooltip text when the current access state of the content is "Open for View"',
	},
	public: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.public',
		defaultMessage: 'Public - Anyone with the link can access',
		description: 'Tooltip text when the current access state of the content is "Public"',
	},
	error: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.error',
		defaultMessage: 'Access level failed to load',
		description: 'Tooltip text when the current access information failed to load',
	},
	accessLinkEnabled: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.access-link-enabled.non-final',
		defaultMessage: 'Confluence access link is on',
		description:
			'Tooltip shown on the share button when a Confluence (internal) access link is enabled for this page',
	},
	publicAccessLinkEnabled: {
		id: 'share-and-restrict-dialog.controlled-share-button.tooltip.public-access-link-enabled.non-final',
		defaultMessage: 'Public access link is on',
		description:
			'Tooltip shown on the share button when a public access link is enabled for this page',
	},
});

const getIconAndTooltipFromData = (
	isPublicLinkOn: boolean,
	hasInheritedRestrictions: boolean,
	hasViewRestrictions: boolean,
	isErrored: boolean,
	isUnpublishedDraft: boolean,
	hasRestrictions: boolean,
	isAccessLinkOn: boolean,
	accessType: ConfluenceShareableLinkAccessType | null,
): [ControlledShareButtonIcon, MessageDescriptor] => {
	if (isErrored) {
		return ['error', tooltipMessages.error];
	}
	if (isAccessLinkOn) {
		return ['internal', tooltipMessages.accessLinkEnabled];
	}
	if (isPublicLinkOn) {
		if (accessType === 'EXTERNAL') {
			return ['public', tooltipMessages.publicAccessLinkEnabled];
		}
		return ['public', tooltipMessages.public];
	}
	if (hasViewRestrictions) {
		return ['restricted', tooltipMessages.restricted];
	}
	if (hasInheritedRestrictions) {
		return ['restricted', tooltipMessages.inherited];
	}
	if (isUnpublishedDraft) {
		return ['open', tooltipMessages.draft];
	}
	if (hasRestrictions) {
		return ['open', tooltipMessages.openView];
	}
	return ['open', tooltipMessages.openEdit];
};

export type ShareAndRestrictButtonData = {
	icon: ControlledShareButtonIcon;
	tooltipMessage: MessageDescriptor;
	refetch: () => void;
	hasRestrictions: boolean;
	hasInheritedRestrictions: boolean;
	hasViewRestrictions: boolean;
	isPublicLinkOn: boolean;
	isUnpublishedDraft: boolean;
	isLoading: boolean;
	error: Error | null;
	pageTitle: string | null | undefined;
	accessType: ConfluenceShareableLinkAccessType | null;
} & Object;

const EXPECTED_FREE_EDITION_PUBLIC_LINK_ERROR =
	'This is a Standard/Premium feature. You are currently subscribed to the Free plan.';

export const useShareAndRestrictButtonData = (
	contentId: string,
	isContentTypePublicLinkEligible: boolean,
	isUnpublishedDraft: boolean,
	contentType: string,
): ShareAndRestrictButtonData => {
	const shouldIncludePublicLink =
		isContentTypePublicLinkEligible && fg('confluence_frontend_usd_ssr')
			? true
			: isContentTypePublicLinkEligible;

	const { data, loading, error, refetch } = useQueryInterop<
		useShareAndRestrictButtonDataQuery$data,
		useShareAndRestrictButtonDataQuery$variables
	>(useShareAndRestrictButtonDataQuery, {
		variables: { contentId, isContentTypePublicLinkEligible: shouldIncludePublicLink },
		fetchPolicy: fg('confluence_frontend_usd_ssr') ? 'cache-and-network' : 'network-only',
		errorPolicy: 'all',
	});

	const errors: Error[] = [];

	if (error?.message.includes(EXPECTED_FREE_EDITION_PUBLIC_LINK_ERROR)) {
		markErrorAsHandled(error);
	} else if (error) {
		errors.push(error);
	}

	const hasRestrictions = Boolean(data?.singleContent?.hasRestrictions);
	const hasInheritedRestrictions = Boolean(data?.singleContent?.hasInheritedRestrictions);
	const hasViewRestrictions = Boolean(data?.singleContent?.hasViewRestrictions);
	const pageTitle = data?.singleContent?.title;
	const isPublicLinkOn = shouldIncludePublicLink
		? data?.publicLinkPage?.pageStatus === 'ON'
		: false;
	const accessType = data?.publicLinkPage?.accessType ?? null;
	const isAccessLinkOn =
		ACCESS_LINK_ENROLLED_CONTENT_TYPES.includes(contentType) &&
		!isUnpublishedDraft &&
		accessType === 'INTERNAL_LICENSED' &&
		data?.publicLinkPage?.pageStatus === 'ON' &&
		expValEquals('cc_perms_mvp_shareable_links_ui', 'isEnabled', true);

	const [icon, tooltipMessage] = getIconAndTooltipFromData(
		isPublicLinkOn,
		hasInheritedRestrictions,
		hasViewRestrictions,
		Boolean(errors.length),
		isUnpublishedDraft,
		hasRestrictions,
		isAccessLinkOn,
		accessType,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'shareAndRestrictButtonQuery',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent]);

	return {
		icon,
		tooltipMessage,
		refetch: () => {
			void refetch?.({ contentId, isContentTypePublicLinkEligible: shouldIncludePublicLink });
		},
		// When navigating in SPA, the cache-and-network fetchPolicy will initially return the data
		// from the previous page if the current one has not been visited before. This check ensures
		// that we show the loading state when the data for this content is not actually available.
		isLoading: loading && !Boolean(data?.singleContent?.id === contentId),
		error: errors.length > 0 ? errors[0] : null,
		hasRestrictions,
		hasInheritedRestrictions,
		hasViewRestrictions,
		isPublicLinkOn,
		pageTitle,
		isUnpublishedDraft,
		accessType,
	};
};
